import { LINE_OA } from 'common/constants/contact'
import { useFeatureEnabled } from 'core/feature-config/api/queries'
import { ScreenType } from 'layouts/dashboard'
import { useGetCreditOrdersCount, useGetCreditWalletProfile } from 'modules/wallets/api/queries'
import { useUserInfo } from 'modules/user/api/queries'
import {
  useAllowFeatures,
  useFirstAllowedDashboardPath,
  useIsPrimaryOwnerAndSecondaryOwnerAndAdmin,
} from 'modules/user/api/selector'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { PATH_DASHBOARD } from 'routes/paths'

import { Link as LinkMaterial, Typography, alpha } from '@mui/material'
import MuiAppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Toolbar from '@mui/material/Toolbar'
import { styled, useTheme } from '@mui/material/styles'

import { Icon } from '@iconify/react'

import Logo from 'components/Logo'

import AccountPopover from './AccountPopover'
import NotificationsPopover from './NotificationsPopover'
import { NAVBAR_MENU, NavbarMenuItem } from './menuConfig'
import { useCreditWalletAccess } from 'modules/wallets/hooks'

const drawerWidth = 240
const appBarWidth = 85

interface AppBarProps {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  screenType: ScreenType
}

const AppBar = styled(MuiAppBar)<{ open?: boolean }>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  borderBottom: `1px solid ${theme.palette.grey[500_12]}`,
  boxShadow: 'none',
  backgroundColor: theme.palette.common.white,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}))

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}))

const formatCreditBalance = (balance: string): string => {
  const n = parseFloat(balance)
  if (isNaN(n)) return '0'
  if (n >= 1_000_000) {
    const v = Math.floor(n / 100_000) / 10
    return `${Number.isInteger(v) ? v : v.toFixed(1)}M`
  }
  if (n >= 1_000) {
    const v = Math.floor(n / 100) / 10
    return `${Number.isInteger(v) ? v : v.toFixed(1)}K`
  }
  return n.toLocaleString()
}

const Navbar: React.FC<AppBarProps> = ({ open, setOpen, screenType }) => {
  const theme = useTheme()
  const { t } = useTranslation('layout')
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = React.useState<{ [key: string]: HTMLElement | null }>({})
  const allowFeatures = useAllowFeatures()
  const isOwner = useIsPrimaryOwnerAndSecondaryOwnerAndAdmin()
  const firstAllowedPath = useFirstAllowedDashboardPath()
  const { data: isOrgOverviewEnabled } = useFeatureEnabled('ORG_OVERVIEW')
  const { data: isIotDeviceEnabled } = useFeatureEnabled('IOT_DEVICE')
  const featureFlags: Record<string, boolean> = {
    ORG_OVERVIEW: Boolean(isOrgOverviewEnabled),
    IOT_DEVICE: Boolean(isIotDeviceEnabled),
  }
  const { canView: canViewCredit } = useCreditWalletAccess()

  const { data: walletProfile } = useGetCreditWalletProfile(canViewCredit)
  const { data: ordersCount } = useGetCreditOrdersCount(canViewCredit)
  const hasPending = canViewCredit && (ordersCount?.statuses.pending ?? 0) > 0
  const creditBalance = walletProfile?.credit_balance
    ? formatCreditBalance(walletProfile.credit_balance)
    : null

  const handleDrawerToggle = () => {
    setOpen((prev) => !prev)
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, menuKey: string) => {
    setAnchorEl((prev) => ({ ...prev, [menuKey]: event.currentTarget }))
  }

  const handleMenuClose = (menuKey: string) => {
    setAnchorEl((prev) => ({ ...prev, [menuKey]: null }))
  }

  const handleMenuItemClick = (path: string, menuKey: string) => {
    navigate(path)
    handleMenuClose(menuKey)
  }

  const getNavbarMenu = (item: NavbarMenuItem) => (
    <React.Fragment key={item.label}>
      <IconButton
        sx={{
          color: theme.palette.common.black,
          borderRadius: 1,
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.25,
        }}
        onClick={(e) => {
          if (item.children) {
            handleMenuOpen(e, item.label)
          } else if (item.onClick) {
            item.onClick(navigate)
          }
        }}
      >
        <img src={item.icon} width={18} height={18} />
        <Box display="flex" alignItems="center" gap={0.25}>
          <Typography variant="caption" sx={{ color: 'inherit', lineHeight: 1 }}>
            {t(item.label)}
          </Typography>
          {item.children && (
            <Icon icon="mdi:chevron-down" width={12} height={12} style={{ color: 'inherit' }} />
          )}
        </Box>
      </IconButton>
      {item.children && (
        <Menu
          anchorEl={anchorEl[item.label]}
          open={Boolean(anchorEl[item.label])}
          onClose={() => handleMenuClose(item.label)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {item.children.map((child) => (
            <MenuItem key={child.path} onClick={() => handleMenuItemClick(child.path, item.label)}>
              {t(child.label)}
            </MenuItem>
          ))}
        </Menu>
      )}
    </React.Fragment>
  )

  return (
    <AppBar
      sx={{
        width:
          open && screenType === 'desktop'
            ? `calc(100% - ${drawerWidth}px)`
            : screenType === 'desktop'
              ? `calc(100% - ${appBarWidth}px)`
              : '100%',
      }}
      position="fixed"
      open={open && screenType === 'desktop'}
    >
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {screenType === 'desktop' ? (
          <Box display="flex" alignItems="center">
            <Stack direction="row" spacing={4} alignItems="center" sx={{ ml: 4 }}>
              {NAVBAR_MENU.map((item) => {
                const permission = allowFeatures.find(
                  (feature) => feature.feature.key === item.permission
                )
                const isPermitted = item.permission ? permission?.crud_permission[1] : true
                const isFeatureEnabled = item.featureFlag ? featureFlags[item.featureFlag] ?? true : true
                const isShow = isPermitted && isFeatureEnabled
                const isShowMemberManagement = isOwner

                return item.label == 'employee'
                  ? isShowMemberManagement && getNavbarMenu(item)
                  : isShow && getNavbarMenu(item)
              })}
            </Stack>
          </Box>
        ) : (
          <>
            <Link to={firstAllowedPath} style={{ textDecoration: 'none' }}>
              <Logo />
            </Link>
            <DrawerHeader sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {screenType === 'mobile' ? (
                <IconButton
                  LinkComponent={LinkMaterial}
                  href={LINE_OA}
                  color="primary"
                  sx={{ px: 0 }}
                >
                  <Icon icon="mdi:help-circle-outline" width={24} height={24} />
                </IconButton>
              ) : (
                <Button
                  LinkComponent={LinkMaterial}
                  href={LINE_OA}
                  startIcon={<Icon icon="mdi:help-circle-outline" width={24} height={24} />}
                >
                  {t('report.problem')}
                </Button>
              )}
              <NotificationsPopover />
              {canViewCredit &&
                (hasPending ? (
                  <Button
                    onClick={() => navigate(PATH_DASHBOARD.wallet.root)}
                    endIcon={<Icon icon="mdi:chevron-right" width={16} />}
                    sx={{
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      color: theme.palette.warning.dark,
                      border: `1px solid ${alpha(theme.palette.warning.main, 0.5)}`,
                      borderRadius: 1,
                      fontWeight: 700,
                      px: 2,
                      whiteSpace: 'nowrap',
                      '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.18) },
                    }}
                  >
                    {t('credit.pending')}
                  </Button>
                ) : creditBalance !== null ? (
                  <Box
                    onClick={() => navigate(PATH_DASHBOARD.wallet.root)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.75,
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { bgcolor: theme.palette.action.hover },
                    }}
                  >
                    <img src="/static/icons/wallets/coin.svg" width={20} height={20} />
                    <Typography variant="body2" fontWeight={700} color="text.primary">
                      {creditBalance}
                    </Typography>
                  </Box>
                ) : null)}
              <IconButton
                onClick={handleDrawerToggle}
                sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}
              >
                {open ? (
                  <img src="/static/icons/layout/sidebar-expand.svg" width={20} height={20} />
                ) : (
                  <img src="/static/icons/layout/sidebar-collapse.svg" width={20} height={20} />
                )}
              </IconButton>
              
            </DrawerHeader>
          </>
        )}
        {screenType === 'desktop' && (
          <Stack direction="row" spacing={2.5} alignItems="center">
            <Button
              LinkComponent={LinkMaterial}
              href={LINE_OA}
              startIcon={<Icon icon="mdi:help-circle-outline" width={24} height={24} />}
            >
              {t('report.problem')}
            </Button>
            <NotificationsPopover />
            {hasPending ? (
              <Button
                onClick={() => navigate(PATH_DASHBOARD.wallet.root)}
                endIcon={<Icon icon="mdi:chevron-right" width={16} />}
                sx={{
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  color: theme.palette.warning.dark,
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.5)}`,
                  borderRadius: 1,
                  fontWeight: 700,
                  px: 2,
                  whiteSpace: 'nowrap',
                  '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.18) },
                }}
              >
                {t('credit.pending')}
              </Button>
            ) : creditBalance !== null ? (
              <Box
                onClick={() => navigate(PATH_DASHBOARD.wallet.root)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.75,
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { bgcolor: theme.palette.action.hover },
                }}
              >
                <img src="/static/icons/wallets/coin.svg" width={20} height={20} />
                <Typography variant="body2" fontWeight={700} color="text.primary">
                  {creditBalance}
                </Typography>
              </Box>
            ) : null}
            <AccountPopover screenType={screenType} />
          </Stack>
        )}
      </Toolbar>
    </AppBar>
  )
}

export default Navbar
